import type { ComponentType, ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { ActionBadge, IconBadge } from '../components/Badges';

interface WidgetCardProps {
  title: string;
  icon: ComponentType<{ className?: string }>;
  /** Small count/badge beside the title */
  badge?: ReactNode;
  action?: { label: string; onClick: () => void };
  loading?: boolean;
  children: ReactNode;
}

/** Shared frame for the Dashboard's list/summary widgets */
export default function WidgetCard({
  title,
  icon,
  badge,
  action,
  loading,
  children,
}: Readonly<WidgetCardProps>) {
  return (
    <section
      aria-label={title}
      className="flex min-h-[220px] flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <header className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <IconBadge icon={icon} size="sm" />
          {title}
          {badge !== undefined && badge !== null && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              {badge}
            </span>
          )}
        </h3>
        {action && (
          <ActionBadge onClick={action.onClick}>{action.label}</ActionBadge>
        )}
      </header>
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-2/3" />
        </div>
      ) : (
        <div className="flex flex-1 flex-col">{children}</div>
      )}
    </section>
  );
}

/** Centered muted line for an empty widget */
export function EmptyLine({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <p className="m-auto py-6 text-center text-sm text-slate-500">{children}</p>
  );
}

/** Title-case an UPPERCASE stored employee name for display */
export function displayEmployee(name: string | null): string {
  if (!name) return 'Unassigned';
  return name.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}
